import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField, SelectField } from "../v3";
import { ProjectService, ToolService } from "../../services";
import { moveToolValidationRules } from "../../pages/tools/validations";

export const MoveTool = ({
  open = false,
  selectedInventory = {},
  companyDetails = {},
  onClose = () => {},
  onSuccess = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [toolMovementDetails, setToolMovementDetails] = useState({
    fromStoreId: undefined,
    toStoreId: undefined,
    movedQuantity: undefined,
    movedById: undefined,
  });

  const { id } = useParams();
  const { user } = useSelector((state) => state.user);

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setToolMovementDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "movedQuantity") {
      const num = parseFloat(value);
      const remainingQuantity = selectedInventory?.count;

      if (value !== "" && (isNaN(num) || num <= 0))
        setErrors((prev) => ({ ...prev, movedQuantity: "Quantity must be greater than 0" }));
      else if (value !== "" && !Number.isInteger(num))
        setErrors((prev) => ({ ...prev, movedQuantity: "Decimal values are not allowed" }));
      else if (num > remainingQuantity)
        setErrors((prev) => ({ ...prev, movedQuantity: `Cannot exceed (${remainingQuantity}) quantities` }));
      else
        setErrors((prev) => {
          const { movedQuantity, ...rest } = prev;
          return rest;
        });
    }
  };

  const handleProjectChange = (selected) => {
    setSelectedProject(selected);
    if (selected?.id === "company-warehouse") {
      setToolMovementDetails((prev) => ({
        ...prev,
        toStoreId: companyDetails.storeId,
      }));
    } else {
      setToolMovementDetails((prev) => ({
        ...prev,
        toStoreId: selected?.storeId || null,
      }));
    }
  };

  const getFilteredProjectOptions = (options, selectedInventory, companyDetails) => {
    let filteredOptions = options;

    // exclude the project whose storeId matches the inventory's storeId
    if (selectedInventory?.storeId) {
      filteredOptions = options.filter((option) => option.storeId !== selectedInventory.storeId);
    }

    // add company warehouse option if the inventory's storeId doesn't match the company's storeId
    if (selectedInventory?.storeId && companyDetails?.storeId && selectedInventory.storeId !== companyDetails.storeId) {
      const companyOption = {
        id: "company-warehouse",
        name: "Company Warehouse",
        storeId: companyDetails.storeId,
      };
      filteredOptions = [companyOption, ...filteredOptions];
    }

    return filteredOptions;
  };

  const handleClearForm = () => {
    setToolMovementDetails({
      fromStoreId: undefined,
      toStoreId: undefined,
      movedQuantity: undefined,
      movedById: undefined,
    });
    setSelectedProject(null);
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const handleMoveTool = async () => {
    setErrors({});

    const validationErrors = moveToolValidationRules(toolMovementDetails);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // validate quantity
    const remainingQuantity = selectedInventory?.count;
    if (toolMovementDetails.movedQuantity > remainingQuantity) {
      setErrors((prev) => ({ ...prev, movedQuantity: `Cannot exceed (${remainingQuantity}) quantities` }));
      return;
    }

    if (toolMovementDetails.movedQuantity) toolMovementDetails.movedQuantity = Number(toolMovementDetails.movedQuantity);
    const payload = {
      ...toolMovementDetails,
      fromStoreId: selectedInventory?.storeId,
      movedById: user?.id,
    };

    try {
      setLoading(true);
      await ToolService.assignTool(id, payload);
      toast.success("Tool moved successfully.");
      onSuccess?.();
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Failed to save task.");
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };
  return (
    <FormDialog
      open={open}
      title={"Move Tool "}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleMoveTool} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <Box sx={{ width: 250 }}>
            <SelectField
              label="Move To"
              value={selectedProject}
              getData={ProjectService.getProjectList}
              getDataStaticFilters={{ status: ["INPROGRESS"] }}
              required
              error={!!errors.toStoreId}
              helperText={errors.toStoreId}
              onChange={handleProjectChange}
              optionFilter={(options) => getFilteredProjectOptions(options, selectedInventory, companyDetails)}
            />
          </Box>
          <Box sx={{ flex: 1, width: 200 }}>
            <InputField
              required
              type="number"
              label="Quantity"
              name="movedQuantity"
              value={toolMovementDetails.movedQuantity}
              onChange={updateDetailsState}
              error={!!errors.movedQuantity}
              helperText={errors.movedQuantity}
              fullWidth
            />
          </Box>
        </Box>
      </Box>
    </FormDialog>
  );
};
